import {
  clearRegardWindowValue,
  getRegardWindowValue,
  setRegardWindowValue,
} from '~/shared/window'

/**
 * Whether the env-gated end-to-end auth bypass is active.
 *
 * Set to `true` only by the Playwright system-test harness
 * (`VITE_E2E_BYPASS_AUTH=true` on the vite dev server it boots — see
 * `playwright.system.config.ts`). When active, `RequireAuth` renders protected
 * content without redirecting to Auth0 and the RTK Query base query skips the
 * bearer-token fetch, so the system test can exercise the real app shell
 * without a live Auth0 session. The env var is unset in normal dev and in every
 * production build, so this constant is `false` and the bypass is fully inert.
 */
export const shouldBypassAuthForE2E =
  import.meta.env.VITE_E2E_BYPASS_AUTH === 'true'

/**
 * Per-deploy shell infrastructure loaded from `/config.json`; app-specific
 * values do not belong here.
 *
 * Each accessor decides whether an omitted value is fatal or disables an
 * optional integration.
 */
export interface RuntimeConfig {
  apiBaseUrl?: string
  auth?: {
    audience?: string
    clientId?: string
    domain?: string
  }
  analytics?: {
    amplitude?: {
      apiKey?: string
    }
    datadog?: {
      applicationId?: string
      clientToken?: string
      env?: string
    }
  }
  flags?: {
    clientKey?: string
  }
}

class RegardConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RegardConfigError'
  }
}

const getStoredRuntimeConfig = (): RuntimeConfig | undefined =>
  getRegardWindowValue('runtimeConfig')

export const setRuntimeConfig = (config: RuntimeConfig): void => {
  setRegardWindowValue('runtimeConfig', config)
}

export const clearRuntimeConfig = (): void => {
  clearRegardWindowValue('runtimeConfig')
}

export const getRuntimeConfig = (): RuntimeConfig => {
  const config = getStoredRuntimeConfig()
  if (config) {
    return config
  }
  throw new RegardConfigError('Runtime config not loaded')
}

export const getApiBaseUrl = (): string => {
  const configuredBaseUrl = getStoredRuntimeConfig()?.apiBaseUrl?.trim()
  if (configuredBaseUrl) {
    return configuredBaseUrl
  }
  // Default to the same-origin `/api` prefix, resolved to an absolute URL so
  // fetch can parse it. In dev and system tests the vite proxy forwards
  // `/api/*` to the atlas backend (stripping the prefix), matching on the
  // path regardless of host. Deploys that talk to a different-origin backend set
  // an absolute `apiBaseUrl` in runtime config to override this.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`
  }
  return '/api'
}

export interface AuthConfig {
  audience?: string
  clientId: string
  domain: string
}

export const getAuthConfig = (): AuthConfig => {
  const auth = getStoredRuntimeConfig()?.auth
  const domain = auth?.domain?.trim()
  const clientId = auth?.clientId?.trim()
  if (!domain || !clientId) {
    throw new RegardConfigError('Auth0 configuration missing: domain/clientId')
  }
  return { audience: auth?.audience?.trim() || undefined, clientId, domain }
}

export interface AmplitudeConfig {
  apiKey: string
}

/**
 * Returns `undefined` when the browser write key is blank, disabling Amplitude.
 */
export const getAmplitudeConfig = (): AmplitudeConfig | undefined => {
  const apiKey = getStoredRuntimeConfig()?.analytics?.amplitude?.apiKey?.trim()
  return apiKey ? { apiKey } : undefined
}

export interface DatadogConfig {
  applicationId: string
  clientToken: string
  env?: string
}

/**
 * Resolves the Datadog RUM configuration, or `undefined` when it is not fully
 * configured.
 *
 * RUM is initialized only when both `applicationId` and `clientToken` are
 * present, so this returns `undefined` whenever either is missing or blank.
 * Callers treat `undefined` as "do not initialize" rather than throwing, since
 * observability is optional and must never block boot.
 */
export const getDatadogConfig = (): DatadogConfig | undefined => {
  const datadog = getStoredRuntimeConfig()?.analytics?.datadog
  const applicationId = datadog?.applicationId?.trim()
  const clientToken = datadog?.clientToken?.trim()
  if (!applicationId || !clientToken) {
    return undefined
  }
  return { applicationId, clientToken, env: datadog?.env?.trim() || undefined }
}

export interface FlagsConfig {
  clientKey: string
}

/**
 * Resolves the Unleash Frontend API configuration, or `undefined` when no client key is set.
 *
 * The deployment supplies this public, read-only browser key. Without it, flags resolve off and the
 * integration reports the missing configuration (Atlas ADR-0028).
 */
export const getFlagsConfig = (): FlagsConfig | undefined => {
  const clientKey = getStoredRuntimeConfig()?.flags?.clientKey?.trim()
  return clientKey ? { clientKey } : undefined
}
